// Handbook — role-specific in-app guide.
// Content is authored per role and rendered as a premium, scrollable Notion-style page
// with a sticky table-of-contents on desktop.
import { useMemo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Sparkles, CalendarDays, Users, MapPin, Building2, ClipboardList,
  Repeat, Clock, Bell, User as UserIcon, Megaphone, FileBarChart, Shield,
  Settings as SettingsIcon, CreditCard, KeyRound, Lightbulb, HelpCircle,
  LayoutDashboard, ChevronRight, Search, X,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import { translateSections } from "@/pages/HandbookContent.i18n";
import { markHandbookViewed } from "@/lib/handbookVersion";

// Mark handbook as viewed on component mount — clears the "Nieuw" badge
// in the sidebar. Kept in useEffect (not module scope) so it only fires
// when the user actually navigates to /handbook, not at app boot.

// ---- Content per role -------------------------------------------------------
// Each section: { id, title, icon, subtitle, articles: [{ h, p }] }
// `p` accepts a string OR an array of strings (rendered as paragraphs) OR
// an array of { list: [] } for bulleted lists.

const ROLE_META = {
  owner:    { key: "handbook_role_owner",    tint: "217 91% 55%" },
  manager:  { key: "handbook_role_manager",  tint: "217 91% 55%" },
  teamlead: { key: "handbook_role_teamlead", tint: "160 65% 45%" },
  employee: { key: "handbook_role_employee", tint: "280 65% 55%" },
};

function content(role) {
  const OWNER_ONLY = [
    {
      id: "billing",
      title: "Abonnement & facturatie",
      icon: CreditCard,
      subtitle: "Alleen jij als eigenaar beheert het abonnement, de facturen en betaalgegevens",
      articles: [
        {
          h: "Wat is inbegrepen?",
          p: [
            "Rostr werkt met één abonnement per bedrijf — alle medewerkers, teamleiders en managers zitten samen onder jouw abonnement.",
            "Je team ziet nooit prijzen, kortingen of facturen: alleen jij als eigenaar hebt toegang tot Abonnement & facturatie.",
            "Rostr biedt een gratis proefperiode; daarna kies je een plan dat past bij je teamgrootte.",
          ],
        },
        {
          h: "Onze 4 plannen (per maand, in euro's)",
          p: [
            "Starter — 2 tot 15 werknemers · €12,99/mnd",
            "Team — 16 tot 40 werknemers · €29,99/mnd (aanbevolen voor de meeste horeca- en retailteams)",
            "Business — 41 tot 100 werknemers · €59,99/mnd",
            "Scale — 101 tot 500 werknemers · €99,99/mnd",
            "Meer dan 500 werknemers of specifieke wensen (SLA, extra rollen, integraties)? Stuur een mail naar rosterhelp@gmail.com voor een plan op maat.",
            "Je kunt op elk moment upgraden of downgraden — de facturatie wordt automatisch pro-rata verrekend.",
          ],
        },
        {
          h: "Waar vind ik het abonnement?",
          p: "In de zijbalk klik je op Instellingen → Abonnement. Boven zie je je huidige status (proefperiode, actief, opengezegd of vergrendeld) en het aantal dagen dat je nog hebt. Daaronder staan de beschikbare plannen.",
        },
        {
          h: "Plan wisselen (upgrade / downgrade)",
          p: [
            "Klik op 'Start abonnement' of 'Beheren' bij het gewenste plan.",
            "Je wordt via een beveiligde Stripe-omgeving door de betaling geleid — je hoeft niets over te typen.",
            "Direct na betaling is het plan actief; je team merkt er niks van behalve dat een eventuele vergrendeling opgeheven wordt.",
          ],
        },
        {
          h: "Facturen & betaalmethode",
          p: "Via de knop 'Beheren' open je het Stripe-klantportaal. Daar download je facturen (PDF), pas je je betaalmethode aan en zie je je volledige betaalgeschiedenis.",
        },
        {
          h: "Opzeggen of pauzeren",
          p: "In het Stripe-klantportaal kun je met één klik opzeggen. Je toegang blijft actief tot het einde van de lopende periode — daarna wordt Rostr vergrendeld tot je een nieuw plan kiest. Er gebeurt niets met je data.",
        },
        {
          h: "App vergrendeld — wat betekent dat?",
          p: [
            "Als je proefperiode voorbij is én er geen actief abonnement is, zien je managers en werknemers een blokkade-scherm.",
            "Alleen jij als eigenaar kan dan nog inloggen en een plan kiezen om iedereen weer aan het werk te krijgen.",
            "Data (roosters, verlof, uren) blijft veilig opgeslagen tijdens de vergrendeling.",
          ],
        },
      ],
    },
    {
      id: "settings",
      title: "Bedrijfsinstellingen",
      icon: SettingsIcon,
      subtitle: "Naam, tijdzone, taal, privacy en uitnodigingsregels",
      articles: [
        {
          h: "Bedrijfsgegevens",
          p: "In Instellingen → Bedrijf pas je bedrijfsnaam, sector en tijdzone aan. Deze verschijnen in de zijbalk, in e-mails naar je team en in exports. Wijzigingen zijn direct actief.",
        },
        {
          h: "Standaardtaal & tijdzone",
          p: "Nieuwe medewerkers krijgen automatisch de standaardtaal die je hier instelt (Nederlands of Engels). Iedereen kan later voor zichzelf iets anders kiezen in Profiel → Voorkeuren.",
        },
        {
          h: "Privacy in de directory",
          p: [
            "Bepaal welke velden werknemers van elkaar zien: telefoon, e-mail, functie, afdeling en verjaardag.",
            "'Geboortejaar verbergen' laat alleen dag/maand zien — aanbevolen voor privacy.",
            "Deze regels gelden alleen tussen werknemers onderling; management ziet altijd alles.",
          ],
        },
        {
          h: "Uitnodigingsregels",
          p: [
            "Bepaal of managers en/of teamleiders zelf uitnodigingscodes mogen aanmaken.",
            "Stel in hoe lang codes geldig blijven (bv. 14 dagen) — vervallen codes werken niet meer.",
            "Codes koppelen automatisch een rol: kies bij het aanmaken of het een medewerker-, teamleider- of manager-uitnodiging is.",
          ],
        },
      ],
    },
  ];

  const MANAGEMENT_SECTIONS = [
    {
      id: "new_features",
      title: "Nieuw in Rostr",
      icon: Sparkles,
      subtitle: "De laatste verbeteringen op een rij — probeer ze uit!",
      articles: [
        {
          h: "Globale zoekbalk (⌘K)",
          p: [
            "Druk overal in de app op ⌘K (Mac) of Ctrl+K (Windows) — of klik op de zoekbalk bovenaan.",
            "Zoek in één seconde naar pagina's, medewerkers of snelle acties (bv. 'nieuwe dienst', 'donker/licht').",
            "Klik op een medewerker in de resultaten en je springt direct naar zijn/haar profielpagina.",
            "Gebruik ↑ ↓ en Enter om zonder muis te navigeren.",
          ],
        },
        {
          h: "Terugkerende diensten",
          p: [
            "Bij het aanmaken van een dienst kun je de schakelaar 'Herhaal wekelijks' aanzetten.",
            "Kies hoeveel weken (max. 26) — Rostr maakt automatisch dezelfde dienst op elk terugkerend moment.",
            "Overlappende terugkerende diensten worden stil overgeslagen, zodat je nooit dubbele planning krijgt.",
          ],
        },
        {
          h: "Automatische overlap-preventie",
          p: [
            "Rostr blokkeert nu automatisch nieuwe diensten die overlappen met een bestaande dienst van dezelfde medewerker.",
            "Voorbeeld: als iemand van 10:00–16:00 werkt, kun je geen tweede dienst 14:00–20:00 op dezelfde dag toevoegen.",
            "Bij overlap opent een duidelijke modal met naam en tijden van de conflicterende dienst.",
          ],
        },
        {
          h: "Swipe-goedkeuring op mobiel",
          p: [
            "Ga naar Aanvragen op je telefoon: je kunt nu naar rechts vegen om goed te keuren en naar links om af te wijzen.",
            "Werkt voor verlof- én urenaanvragen — bespaart je klikwerk als je onderweg bent.",
            "De klassieke ✓ en ✕ knoppen zitten er ook nog voor precisie.",
          ],
        },
        {
          h: "Offline modus (PWA)",
          p: [
            "Rooster, locaties en dashboard-cijfers blijven zichtbaar ook zonder internet.",
            "Boven in beeld verschijnt een geel bandje 'Je bent offline' — zodat je weet dat de data van je laatste bezoek getoond wordt.",
            "Zodra je weer online bent, laadt de app automatisch de nieuwste versie.",
          ],
        },
        {
          h: "Menu sluit automatisch",
          p: "Op mobiel schuift de zijbalk vanzelf dicht zodra je op een menu-item klikt. Rustiger scherm, minder tikken.",
        },
      ],
    },
    {
      id: "welcome",
      title: "Aan de slag",
      icon: Sparkles,
      subtitle: "In 5 minuten van eerste login tot eerste gepubliceerd rooster",
      articles: [
        {
          h: "1. Nodig je team uit",
          p: [
            "Ga naar Medewerkers → knop 'Uitnodigingscode'.",
            "Kies een rol: Medewerker, Teamleider of Manager.",
            "Deel de code (WhatsApp, mail, bordje in de kantine). De persoon vult 'm in op /join, kiest een wachtwoord óf logt in met Google en is meteen actief.",
            "Codes verlopen na het aantal dagen dat je in Instellingen hebt ingesteld.",
          ],
        },
        {
          h: "2. Maak locaties & teams",
          p: [
            "Locaties = fysieke plekken (vestiging, filiaal, project). Elke locatie krijgt een naam, adres en kleur — die kleur zie je terug in het rooster.",
            "Teams = groepen medewerkers binnen je bedrijf (bv. Keuken, Bediening, Sales). Handig om snel te filteren en om diensten aan een team toe te wijzen.",
          ],
        },
        {
          h: "3. Bouw je eerste rooster",
          p: "Open Rooster → klik in een lege cel om een dienst toe te wijzen. Je kunt medewerker direct kiezen, of leeg laten (open dienst) zodat je later toewijst via 'Snel toewijzen'.",
        },
        {
          h: "4. Publiceer de week",
          p: "Zolang de status 'Concept' is, ziet niemand het rooster. Klik op 'Publiceer week' — pas dan krijgen je medewerkers een pushbericht en zien ze hun diensten in Mijn rooster.",
        },
        {
          h: "5. Personaliseer je dashboard",
          p: "Klik op 'Widgets aanpassen' rechtsboven op het Dashboard. Kies welke stats, lijsten en snelle acties je wilt zien. Je keuze wordt per gebruiker onthouden.",
        },
      ],
    },
    {
      id: "dashboard",
      title: "Dashboard",
      icon: LayoutDashboard,
      subtitle: "Jouw controlepaneel — 25+ widgets die je zelf kiest en herschikt",
      articles: [
        {
          h: "Widgets toevoegen of verwijderen",
          p: [
            "Klik op de knop 'Aanpassen' rechtsboven het dashboard.",
            "Widgets zijn ingedeeld in zeven categorieën: Kerncijfers, Mijn werk, Team, Planning, Aanvragen, Updates en Snelle acties.",
            "Vink aan wat je wilt zien (maximaal 20 tegelijk). Je persoonlijke keuze wordt lokaal opgeslagen — collega's zien hun eigen selectie.",
            "Klik op 'Standaard herstellen' om terug te gaan naar de aanbevolen set voor jouw rol.",
          ],
        },
        {
          h: "Kerncijfers-widgets",
          p: [
            "Medewerkers — totaal aantal actieve accounts.",
            "Diensten deze week — alle geplande diensten van de komende 7 dagen.",
            "Openstaande diensten — diensten zonder toegewezen medewerker.",
            "Verlof / Ruilen / Uren wachtend — hoeveel aanvragen nog op jouw akkoord wachten.",
            "Locaties en Teams — snelle tellers.",
            "Totaal uren week — alle geplande uren opgeteld.",
          ],
        },
        {
          h: "Team-widgets",
          p: [
            "Team vandaag — wie werkt er nu en op welke tijden.",
            "Verjaardagen — wie is er jarig deze week (met snelle feliciteer-knop).",
            "Top uren per medewerker — top 5 met de meeste uren deze week.",
          ],
        },
        {
          h: "Manager-specifieke widgets",
          p: [
            "Onbezet-alarm — open diensten binnen 48 uur. Rood als het onder 24 uur zit.",
            "Publish-status — welk percentage van je week al gepubliceerd is, per dag.",
            "Rooster-horizon — hoeveel dagen vooruit je rooster reikt (goede check voor lange termijn planning).",
            "Verlof genomen dit jaar — top 5 medewerkers met meeste verlofdagen.",
            "Broadcast composer — direct een melding sturen vanaf je dashboard.",
          ],
        },
        {
          h: "Slepen & herschikken",
          p: "Op desktop kun je widgets slepen om de volgorde te veranderen. Ze onthouden hun positie voor de volgende keer.",
        },
      ],
    },
    {
      id: "roster",
      title: "Rooster maken",
      icon: CalendarDays,
      subtitle: "De hoofd-planning: week-, dag- en per-medewerker weergave",
      articles: [
        {
          h: "Weergaves wisselen",
          p: [
            "Per dag — chronologische weergave met alle diensten van één dag.",
            "Per week — klassieke matrix (medewerker × weekdagen). Ideaal voor overzicht.",
            "Per medewerker — één rij per persoon voor snelle vergelijking.",
            "Wissel bovenaan met de tabs; je keuze wordt onthouden.",
          ],
        },
        {
          h: "Dienst toevoegen",
          p: [
            "Klik op een lege cel of gebruik 'Dienst toevoegen' rechtsboven.",
            "Vul in: datum, begin- en eindtijd, medewerker (of leeg voor open dienst), locatie, team, functie en eventueel een opmerking.",
            "De opmerking is zichtbaar voor de medewerker bij zijn dienst — perfect voor briefing, taken of extra info.",
          ],
        },
        {
          h: "Open diensten (nog niet toegewezen)",
          p: [
            "Laat het veld 'Medewerker' leeg — de dienst verschijnt als 'Onbezet' in het rooster.",
            "Open diensten zie je terug in 'Snel toewijzen' (mobiel) en in de widget 'Open diensten'.",
            "Werknemers kunnen open diensten NIET zelf claimen — jij als manager wijst toe.",
          ],
        },
        {
          h: "Kopiëren & plakken",
          p: [
            "Kopieer vorige week — dupliceer alles van vorige week (als concept) naar deze week.",
            "Kopieer naar volgende week — spiegel deze week door.",
            "Kies dagen — selecteer specifieke brondagen en één of meerdere doeldagen (bv. maandag → di+wo+do).",
            "Bewaar toegewezen medewerkers of laat de doeldagen leeg. Overlappende diensten worden overgeslagen met een popup-overzicht.",
          ],
        },
        {
          h: "Dienst dupliceren (+1 dag)",
          p: "Klik op een dienst → 'Dupliceer +1 dag'. Maakt een kopie op de volgende dag met dezelfde tijden en toewijzing. Handig voor terugkerende ochtenddiensten.",
        },
        {
          h: "Publiceren",
          p: [
            "Zolang de status 'Concept' is, ziet de werknemer niks — je kunt rustig schuiven.",
            "Zet de status van een dienst op 'Gepubliceerd' óf gebruik 'Publiceer week' bovenaan om alles ineens live te zetten.",
            "Bij publicatie krijgen betrokken medewerkers een push- en e-mailmelding (afhankelijk van hun voorkeuren).",
          ],
        },
        {
          h: "Filters gebruiken",
          p: "Filter op locatie of team via de knoppen rechtsboven. Zo hou je overzicht als je in meerdere vestigingen tegelijk plant.",
        },
      ],
    },
    {
      id: "employees",
      title: "Medewerkers",
      icon: Users,
      subtitle: "Team beheren, profielen bekijken en uitnodigen",
      articles: [
        {
          h: "Overzicht",
          p: "In de sidebar → Medewerkers zie je iedereen die bij je bedrijf hoort. Zoek op naam, functie of afdeling. Klik op een rij om het profiel te openen.",
        },
        {
          h: "Uitnodigen",
          p: [
            "Knop 'Uitnodigingscode' rechtsboven → kies de rol (Medewerker, Teamleider of Manager).",
            "De code wordt gekopieerd; deel 'm via WhatsApp, e-mail of print 'm uit.",
            "De persoon opent /join, vult de code in, kiest wachtwoord of Google-login en is direct onderdeel van je bedrijf.",
          ],
        },
        {
          h: "Profiel-detail",
          p: [
            "Overzicht — contact, functie, verjaardag, aankomende diensten.",
            "Verlof — al zijn/haar verlofaanvragen (status + beslissingen).",
            "Ruilen — alle verstuurde en ontvangen wisselverzoeken.",
            "Alleen jij als management kunt de gegevens bewerken; werknemers hebben een read-only profiel.",
          ],
        },
        {
          h: "Wat mag je aanpassen?",
          p: [
            "Naam, functietitel, afdeling, telefoon, geboortedatum.",
            "Locatie en team-toewijzing.",
            "Rol (Werknemer / Teamleider / Manager) — behalve die van jezelf en de eigenaar.",
            "Taal en meldingsvoorkeuren op verzoek van de medewerker.",
          ],
        },
        {
          h: "Rollen en rechten",
          p: [
            "Medewerker → alleen eigen rooster, verlof, ruil en beschikbaarheid.",
            "Teamleider → als medewerker + goedkeuren van verlof/ruil/uren en rapportages inzien.",
            "Manager → alles wat een teamleider mag + medewerkers uitnodigen/bewerken + rooster maken.",
            "Eigenaar → alles + abonnement en bedrijfsinstellingen.",
          ],
        },
      ],
    },
    {
      id: "locations",
      title: "Locaties",
      icon: MapPin,
      subtitle: "Vestigingen, kleuren en adressen",
      articles: [
        {
          h: "Waarom locaties?",
          p: "Elke dienst wordt gekoppeld aan een locatie. Zo weet de medewerker waar hij/zij verwacht wordt en kun jij makkelijk filteren en rapporteren per vestiging.",
        },
        {
          h: "Locatie toevoegen",
          p: [
            "Sidebar → Locaties → knop 'Locatie toevoegen'.",
            "Vul in: naam (bv. 'Amsterdam Centrum'), adres, plaats en een kleur.",
            "De kleur zie je terug als accent in het rooster — handig om vestigingen visueel te onderscheiden.",
          ],
        },
        {
          h: "Werknemers aan een locatie koppelen",
          p: "In het medewerkerprofiel kies je een primaire locatie. Dat gebruiken we als voorstel bij nieuwe diensten (maar je kunt altijd afwijken).",
        },
      ],
    },
    {
      id: "teams",
      title: "Teams",
      icon: Building2,
      subtitle: "Groepen medewerkers voor snelle filtering",
      articles: [
        {
          h: "Wat is een team?",
          p: "Een team is een logische groep binnen je bedrijf: Keuken, Bediening, Kassa, IT, Sales… Één medewerker kan bij één team horen.",
        },
        {
          h: "Team toevoegen",
          p: "Sidebar → Teams → 'Team toevoegen'. Geef 'm een naam en optioneel een kleur en omschrijving. Vervolgens koppel je medewerkers vanuit hun profiel of via de team-lijst.",
        },
        {
          h: "Team-filters in rooster",
          p: "In het Rooster gebruik je de team-filter om alleen bv. 'Bediening' te zien — ideaal voor grote teams waar meerdere afdelingen tegelijk werken.",
        },
      ],
    },
    {
      id: "requests",
      title: "Aanvragen goedkeuren",
      icon: ClipboardList,
      subtitle: "Verlof, dienstwissels en uren beoordelen",
      articles: [
        {
          h: "Verlofaanvragen",
          p: [
            "Sidebar → Verlofaanvragen → tab 'Wachtend' toont alles wat nog beslist moet worden.",
            "Klik op 'Goedkeuren' of 'Afwijzen'. Voeg optioneel een notitie toe — die zien alleen jij en de aanvrager.",
            "Bij goedkeuring worden ingeplande diensten in die periode automatisch verwijderd (of gemarkeerd voor herplanning).",
            "De medewerker krijgt direct een push- en e-mailmelding.",
          ],
        },
        {
          h: "Dienstwissels beoordelen",
          p: [
            "Werknemer A vraagt B om zijn dienst over te nemen → B accepteert of wijst af.",
            "Zodra B accepteert komt de wissel bij jou terecht met status 'Geaccepteerd — wacht op manager'.",
            "Klik op 'Goedkeuren' → de dienst wordt automatisch overgeschreven op B en beide krijgen bevestiging.",
            "Je kunt ook afwijzen; dan blijft de originele dienst staan bij A.",
          ],
        },
        {
          h: "Mobiele Manager-tools",
          p: "Op je telefoon zie je onder de bottom-nav 'Aanvragen' — hier keur je verlof en ruilverzoeken goed vanuit één tabbed scherm. Perfect als je onderweg bent.",
        },
      ],
    },
    {
      id: "reports",
      title: "Rapportages & CSV-export",
      icon: FileBarChart,
      subtitle: "Voor loonverwerking, HR en analyse",
      articles: [
        {
          h: "Wat kun je exporteren?",
          p: [
            "Uren-rapport — alle ingediende/goedgekeurde uren per medewerker in een periode.",
            "Rooster-rapport — alle geplande diensten met medewerker, locatie en team.",
            "Meer exports (verlof, ruilverzoeken) komen in aankomende updates.",
          ],
        },
        {
          h: "Rapport downloaden",
          p: [
            "Sidebar → Rapportages → kies rapporttype.",
            "Selecteer een periode (deze week, vorige week, deze maand of custom).",
            "Klik 'Download CSV' — het bestand opent direct in Excel of Google Sheets.",
            "Kolommen zijn kant-en-klaar voor je salarisadministratie.",
          ],
        },
      ],
    },
    {
      id: "news",
      title: "Nieuwsfeed",
      icon: Megaphone,
      subtitle: "Bedrijfsbrede aankondigingen en verjaardagen",
      articles: [
        {
          h: "Wanneer een bericht plaatsen?",
          p: "Nieuws is voor iedereen zichtbaar. Gebruik het voor openingstijden-wijzigingen, teamevenementen, personeelsmededelingen of feestdagen. Werknemers krijgen een pushbericht en zien het bovenaan hun nieuwsfeed.",
        },
        {
          h: "Bericht schrijven",
          p: [
            "Sidebar → Nieuws → 'Nieuw bericht'.",
            "Titel + bericht (rijke tekst).",
            "Optie 'Vastpinnen bovenaan' — voor belangrijke updates die langer moeten opvallen.",
            "Je kunt eigen berichten later bewerken of verwijderen; de eigenaar mag berichten van anderen ook aanpassen.",
          ],
        },
        {
          h: "Verjaardagen",
          p: [
            "Rostr toont automatisch wie er vandaag jarig is en wie er deze maand jarig wordt (afhankelijk van de privacy-instellingen).",
            "Collega's kunnen op een 'Feest mee'-knop drukken bij de jarige — die krijgt dan een persoonlijke felicitatie-melding.",
          ],
        },
        {
          h: "Wegveegde berichten",
          p: "Werknemers kunnen berichten voor zichzelf verbergen door naar links te swipen. Hun eigen wegveeg blijft privé — het bericht blijft voor iedereen zichtbaar behalve voor hen.",
        },
      ],
    },
    {
      id: "notifications",
      title: "Meldingen sturen & ontvangen",
      icon: Bell,
      subtitle: "Push, e-mail en in-app op één plek",
      articles: [
        {
          h: "Zelf een broadcast sturen",
          p: [
            "Sidebar → Meldingen → 'Melding sturen'.",
            "Kies doelgroep: iedereen, of specifiek per rol (bv. alleen teamleiders).",
            "Titel + bericht + optioneel 'Ook e-mailen' aanvinken.",
            "Iedereen in de doelgroep krijgt de melding meteen op alle apparaten waar hij is ingelogd.",
          ],
        },
        {
          h: "Automatische meldingen",
          p: [
            "Rooster gepubliceerd of jouw dienst gewijzigd.",
            "Verlofaanvraag beslist.",
            "Ruilverzoek ontvangen / goedgekeurd / afgewezen.",
            "Rol-mismatch bij toewijzing (alleen management).",
            "Verjaardag van een collega (uitschakelbaar).",
          ],
        },
        {
          h: "Kanalen instellen",
          p: "Elke gebruiker bepaalt zelf via Profiel → Voorkeuren welke categorie via welk kanaal komt (app, e-mail, push). Push werkt ook als de app dicht is, mits toegevoegd aan het startscherm (PWA).",
        },
      ],
    },
    {
      id: "mobile-tools",
      title: "Manager-tools op mobiel",
      icon: LayoutDashboard,
      subtitle: "Wat je onderweg kunt regelen vanaf je telefoon",
      articles: [
        {
          h: "Team vandaag",
          p: [
            "Bottom-nav → 'Vandaag'.",
            "Toont drie groepen: wie nu werkt, wie later vandaag komt, en wie al klaar is.",
            "Ideaal voor snel checken tijdens een pauze of onderweg naar de zaak.",
          ],
        },
        {
          h: "Snel toewijzen",
          p: [
            "Bottom-nav → 'Toewijzen'.",
            "Toont alle open diensten van de komende 7 dagen.",
            "Tik op een dienst → kies een medewerker uit de lijst met beschikbaarheids-check ('Vrij' / 'Overlap' / 'Andere rol').",
            "Toewijzen gebeurt direct — de medewerker krijgt een melding.",
          ],
        },
        {
          h: "Aanvragen (mobiel)",
          p: "Bottom-nav → 'Aanvragen' — verlof, ruilverzoeken en uren-goedkeuring in één scherm met tabs. Perfect voor snel afhandelen tussendoor.",
        },
        {
          h: "Wat werkt alleen op desktop?",
          p: "Rooster maken en grote wijzigingen (bulk-kopie, matrix-view, exports, bedrijfsinstellingen, uitnodigingen) doe je op de website — daar heb je overzicht en snelheid.",
        },
      ],
    },
    {
      id: "my-work",
      title: "Mijn eigen werk",
      icon: UserIcon,
      subtitle: "Ja, als manager plan je ook je eigen diensten in",
      articles: [
        {
          h: "Mijn werk in de sidebar",
          p: [
            "Onder 'Mijn werk' vind je precies dezelfde tools als je werknemers hebben:",
            "Mijn rooster — jouw eigen diensten.",
            "Mijn verlof — vraag zelf ook verlof aan (jouw manager of de eigenaar keurt goed).",
            "Mijn ruilverzoeken — ruil met een collega net als iedereen.",
            "Beschikbaarheid — laat weten wanneer jij niet kunt werken.",
          ],
        },
      ],
    },
    {
      id: "profile",
      title: "Profiel & voorkeuren",
      icon: UserIcon,
      subtitle: "Jouw account, taal en thema",
      articles: [
        {
          h: "Persoonsgegevens",
          p: "Onder je naam in de sidebar → Profiel. Hier pas je telefoon, functietitel, afdeling en wachtwoord aan.",
        },
        {
          h: "Voorkeuren",
          p: [
            "Taal — kies uit 2 talen (Nederlands en Engels); wisselt direct het hele scherm.",
            "Thema — licht of donker (alleen voor jou).",
            "Meldingen — zet per categorie kanalen aan/uit; test met de knop 'Test-melding' of push werkt.",
          ],
        },
      ],
    },
    {
      id: "account_security",
      title: "Account & beveiliging",
      icon: KeyRound,
      subtitle: "Wachtwoord, sessies, 2FA en veilig uitloggen",
      articles: [
        {
          h: "Wachtwoord wijzigen",
          p: "Klik op je avatar → Profiel → sectie 'Beveiliging'. Voer je huidige wachtwoord in, kies een nieuw wachtwoord van minimaal 8 tekens (mix van letters, cijfers en een symbool wordt aanbevolen).",
        },
        {
          h: "Wachtwoord vergeten",
          p: [
            "Op de login-pagina klik je op 'Wachtwoord vergeten?'.",
            "Vul je e-mailadres in — je krijgt binnen 1 minuut een reset-link (check spam als je 'm niet ziet).",
            "De link is 30 minuten geldig. Klik → nieuw wachtwoord → login opnieuw.",
          ],
        },
        {
          h: "Uitloggen op alle apparaten",
          p: "Profiel → Beveiliging → 'Log uit op alle apparaten'. Handig als je telefoon kwijt bent of een gedeeld apparaat hebt gebruikt.",
        },
        {
          h: "Wie kan mijn gegevens zien?",
          p: [
            "Andere managers en de eigenaar zien: naam, foto, uurloon, dienstverleden, verlof en beschikbaarheid.",
            "Team-leden (medewerkers) zien: naam en foto van collega's, geen persoonlijke financiële data.",
            "Rostr deelt niks met derden — alle data blijft binnen jouw bedrijf.",
          ],
        },
      ],
    },
    {
      id: "roles_permissions",
      title: "Rollen & rechten",
      icon: Shield,
      subtitle: "Wie mag wat in Rostr",
      articles: [
        {
          h: "Eigenaar",
          p: [
            "Volledige toegang: bedrijfsinstellingen, facturatie, uurlonen, pauze-regels, alle rooster-acties.",
            "Enige rol die medewerkers kan hernoemen tussen manager/teamleider/medewerker.",
            "Enige rol die de organisatie kan verwijderen of het abonnement kan opzeggen.",
          ],
        },
        {
          h: "Manager",
          p: [
            "Kan het rooster maken/publiceren, verlof- en urenaanvragen goedkeuren, medewerkers uitnodigen en beheren.",
            "Ziet Loonoverzicht (Payroll) maar kan géén uurlonen per persoon aanpassen — dat mag alleen de eigenaar.",
            "Kan geen locaties of teams aanmaken/verwijderen (alleen eigenaar).",
          ],
        },
        {
          h: "Team-leider",
          p: "Zoals manager maar met beperktere financiële toegang: geen Loonoverzicht, wel goedkeuring van verlof/uren voor eigen team.",
        },
        {
          h: "Medewerker",
          p: "Ziet alleen eigen rooster, kan verlof + ruilverzoeken indienen, uren corrigeren. Kan geen dingen wijzigen voor anderen.",
        },
        {
          h: "Rol wijzigen",
          p: "Eigenaar: Medewerkers → open profiel → klik op rol-badge → kies nieuwe rol. Wijziging is direct actief.",
        },
      ],
    },
    {
      id: "faq_management",
      title: "Veelgestelde vragen",
      icon: HelpCircle,
      subtitle: "Snelle antwoorden op alledaagse vragen",
      articles: [
        {
          h: "Hoe werkt automatische pauze precies?",
          p: [
            "Ga naar Pauze (in Inzichten & updates) — daar zie je live hoe jouw regels uitpakken.",
            "Vast: 'X min pauze zodra iemand ≥ Y uur werkt'. Getrapt: verschillende pauze-lengtes bij verschillende dienst-duren.",
            "Rostr trekt de pauze automatisch af van bruto-uren → netto uren voor uitbetaling.",
          ],
        },
        {
          h: "Waar zie ik hoeveel iedereen deze maand verdient?",
          p: "Loonoverzicht (Wallet-icoon in Inzichten). Filter per week/maand, exporteer als CSV, PDF-print voor je boekhouder.",
        },
        {
          h: "Hoe pas ik een uurloon aan?",
          p: "Alleen als eigenaar: Medewerkers → open profiel → 'Uurloon (€/u)' → typ nieuw bedrag → Opslaan. Werkt met terugwerkende kracht vanaf de wijziging.",
        },
        {
          h: "Kan ik een dienst wekelijks laten herhalen?",
          p: "Ja — bij het aanmaken van een dienst zet je 'Herhaal wekelijks' aan en kies je hoeveel weken (max 26). Overlappingen worden stil overgeslagen.",
        },
        {
          h: "Wat als twee diensten overlappen?",
          p: "Rostr blokkeert automatisch: bij opslaan verschijnt een modal met naam + tijden van de conflicterende dienst. Pas start/eind aan om verder te gaan.",
        },
        {
          h: "Hoe voeg ik snel een medewerker/locatie/team toe zonder van pagina te wisselen?",
          p: "Dashboard → widget 'Snelle acties' → klik op de gewenste actie → popup opent → invullen → Opslaan. Sneller dan naar de betreffende pagina navigeren.",
        },
        {
          h: "Kan een medewerker een dienst zelf inruilen?",
          p: "Ja. Medewerker gaat naar Mijn rooster → klikt dienst → 'Ruilen' → kiest collega. Jij als manager keurt de swap definitief goed via Dienstwissels.",
        },
        {
          h: "Wat als iemand ziek is?",
          p: "Twee opties: (1) verwijder de dienst en zoek vervanger via Snel toewijzen of Open diensten. (2) Laat de dienst staan en registreer intern als 'ziek' in profiel-notities. Verwijderde diensten belanden in de Prullenbak — 30 dagen herstelbaar.",
        },
        {
          h: "Hoe stuur ik een bericht aan iedereen tegelijk?",
          p: "Dashboard → 'Snelle acties' → 'Bericht rondsturen' → titel + tekst → Verstuur. Iedereen krijgt een in-app melding (en push als ze dat aan hebben staan).",
        },
        {
          h: "Ik heb per ongeluk iets verwijderd — te herstellen?",
          p: "Meestal wel! Ga naar Instellingen → Prullenbak. Verwijderde diensten en medewerkers blijven daar 30 dagen staan met een 'Herstel'-knop.",
        },
      ],
    },
  ];

  const EMPLOYEE_SECTIONS = [
    {
      id: "new_features",
      title: "Nieuw in Rostr",
      icon: Sparkles,
      subtitle: "Wat er onlangs is verbeterd voor jou",
      articles: [
        {
          h: "Globale zoekbalk (⌘K)",
          p: [
            "Op de desktop-versie klik je bovenaan op de zoekbalk (of druk ⌘K / Ctrl+K).",
            "Vind snel je rooster, verlof, ruilverzoeken of profiel zonder door menu's te klikken.",
            "Werkt ook met toetsenbord: ↑ ↓ om te navigeren, Enter om te openen.",
          ],
        },
        {
          h: "Duidelijker dienstkaartjes",
          p: [
            "Elke dienst laat nu groter en rustiger zien: begin- en eindtijd, pauze, en netto uren op één regel.",
            "Vrije dagen zijn nu compact — geen grote lege blokken meer als je een dag niet werkt.",
            "Een hele week zonder diensten toont alleen een vriendelijke lege-inbox illustratie met de melding 'nog geen diensten'.",
          ],
        },
        {
          h: "Offline blijven werken (PWA)",
          p: [
            "Voeg Rostr toe aan je startscherm en gebruik 'm zonder verbinding.",
            "Je laatst geladen rooster blijft zichtbaar in de metro of in gebieden zonder bereik.",
            "Een geel bandje bovenaan zegt 'Je bent offline' zodat je weet dat de gegevens niet vers zijn.",
          ],
        },
        {
          h: "Menu sluit vanzelf",
          p: "Op je telefoon glijdt het menu automatisch dicht zodra je op iets klikt — zoals bij het openen. Geen extra tik nodig.",
        },
      ],
    },
    {
      id: "welcome",
      title: "Welkom bij Rostr",
      icon: Sparkles,
      subtitle: "Wat kun je hier zelf regelen?",
      articles: [
        {
          h: "Waarvoor is Rostr?",
          p: "Rostr is de app waarin je werkgever je diensten inplant. Jij ziet je rooster, kunt verlof aanvragen, met collega's ruilen en je beschikbaarheid doorgeven — alles op één plek en op elk apparaat.",
        },
        {
          h: "Hoe navigeer je?",
          p: "Onderin de app zie je een menubalk: Rooster · Verlof · Ruilen · Nieuws · Profiel. Op desktop staat de menubalk links en zie je meer statistiek in het dashboard.",
        },
        {
          h: "Rostr op je telefoon (PWA)",
          p: [
            "iPhone: open Rostr in Safari → tik op de deel-knop → 'Zet op beginscherm'.",
            "Android: Chrome vraagt automatisch 'Toevoegen aan startscherm'.",
            "Vanaf je startscherm werkt Rostr als een echte app inclusief pushmeldingen.",
          ],
        },
      ],
    },
    {
      id: "my-roster",
      title: "Mijn rooster",
      icon: CalendarDays,
      subtitle: "Je diensten voor de komende weken",
      articles: [
        {
          h: "Wat zie je?",
          p: "Alle gepubliceerde diensten die op jouw naam staan. Elke dienst toont datum, tijd, locatie en (soms) een opmerking van je manager met extra info of taken.",
        },
        {
          h: "Vandaag & aankomende dagen",
          p: "De bovenste kaart toont je dienst van vandaag met exacte tijden en met wie je samenwerkt. Daaronder verschijnen je aankomende diensten voor deze week.",
        },
        {
          h: "Wat als er geen diensten staan?",
          p: "Dan is je week nog niet gepubliceerd óf sta je niet ingeroosterd. Neem contact op met je manager als je vragen hebt.",
        },
      ],
    },
    {
      id: "leave",
      title: "Verlof aanvragen",
      icon: ClipboardList,
      subtitle: "Vakantie, ziek of andere afwezigheid",
      articles: [
        {
          h: "Hoe vraag je verlof aan?",
          p: [
            "Ga naar Mijn verlof → knop 'Verlof aanvragen'.",
            "Kies type: Vakantie, Ziek, Persoonlijk, Onbetaald of Overig.",
            "Kies start- en einddatum. Het aantal dagen wordt automatisch berekend.",
            "Voeg een korte reden toe (optioneel). Je manager kan een notitie meegeven bij zijn beslissing.",
          ],
        },
        {
          h: "Statussen begrijpen",
          p: [
            "Wachtend — je manager heeft nog niet beslist. Je kunt de aanvraag zelf annuleren.",
            "Goedgekeurd — je bent vrij op deze dagen; ingeplande diensten worden herzien door je manager.",
            "Afgewezen — je manager licht toe via een notitie (of vraag persoonlijk om toelichting).",
          ],
        },
        {
          h: "Bolletjes bij de tabs",
          p: "De tellers boven de tabs tonen nieuwe beslissingen die je nog niet hebt gezien. Zodra je op een tab klikt verdwijnen ze — zo weet je precies waar iets nieuws is.",
        },
      ],
    },
    {
      id: "swaps",
      title: "Diensten ruilen",
      icon: Repeat,
      subtitle: "Ruil een dienst met een collega",
      articles: [
        {
          h: "Zelf een ruilverzoek versturen",
          p: [
            "Vanuit Mijn rooster klik je op een dienst → 'Ruilen'.",
            "Kies een specifieke collega óf laat leeg zodat iedereen het verzoek ziet.",
            "Voeg een berichtje toe (bv. 'Familiebezoek — kan iemand vrijdagochtend overnemen?').",
            "Je verzoek staat op 'Wachtend' tot iemand accepteert.",
          ],
        },
        {
          h: "Een verzoek beantwoorden",
          p: "Mijn ruilverzoeken → tab 'Ontvangen'. Klik Accepteren of Afwijzen. Bij accepteren komt de dienst pas écht bij jou als je manager 'm goedkeurt.",
        },
        {
          h: "Statussen begrijpen",
          p: [
            "Wachtend — nog geen collega gereageerd.",
            "Geaccepteerd — collega akkoord, wacht op manager-goedkeuring.",
            "Goedgekeurd — dienst is officieel overgezet.",
            "Afgewezen / Geannuleerd — verzoek is niet doorgegaan.",
          ],
        },
      ],
    },
    {
      id: "availability",
      title: "Beschikbaarheid",
      icon: Clock,
      subtitle: "Wanneer wil of kan je niet werken?",
      articles: [
        {
          h: "Waarom invullen?",
          p: "Je manager gebruikt je beschikbaarheid om je slimmer in te plannen. Geef bijvoorbeeld aan: 'maandagochtend nooit', of 'weekend alleen na 18:00'.",
        },
        {
          h: "Hoe stel je het in?",
          p: "Ga naar Mijn beschikbaarheid → vink per weekdag aan wanneer je vrij bent (groen vinkje) of niet (rood kruisje). Je manager ziet dit direct bij het plannen.",
        },
        {
          h: "Uitzonderingen",
          p: "Voor eenmalige uitzonderingen (bv. arts-afspraak volgende week) kun je beter direct met je manager overleggen of verlof aanvragen. Beschikbaarheid is voor structurele patronen.",
        },
      ],
    },
    {
      id: "employee_account",
      title: "Account & wachtwoord",
      icon: KeyRound,
      subtitle: "Alles rondom inloggen, wachtwoord en privacy",
      articles: [
        {
          h: "Wachtwoord wijzigen",
          p: "Ga naar Profiel → sectie 'Beveiliging' → vul je huidige + nieuwe wachtwoord in. Minimaal 8 tekens; sterker = beter (mix letters/cijfers/symbolen).",
        },
        {
          h: "Wachtwoord vergeten",
          p: "Op de login-pagina klik je 'Wachtwoord vergeten?'. Vul je e-mail in → check je inbox (en spam-map). De reset-link is 30 minuten geldig.",
        },
        {
          h: "Ik krijg geen reset-mail",
          p: "1. Check spam/junk. 2. Probeer een paar minuten later opnieuw. 3. Vraag je manager of je e-mailadres in Rostr klopt (Medewerkers → jouw profiel). Meestal ligt het aan een typefout in het e-mailadres.",
        },
        {
          h: "Kan ik mijn e-mailadres zelf wijzigen?",
          p: "Nee, uit veiligheids-oogpunt niet. Vraag je manager om dit voor je aan te passen in je profiel.",
        },
        {
          h: "Uitloggen op alle apparaten",
          p: "Profiel → Beveiliging → 'Log uit op alle apparaten'. Doe dit als je telefoon kwijt is of je op een gedeeld apparaat hebt ingelogd.",
        },
        {
          h: "Wie ziet mijn gegevens?",
          p: "Jouw manager en de eigenaar zien je naam, foto, uurloon, dienstverleden, verlof en beschikbaarheid. Collega's zien alleen je naam, foto en met wie ze wanneer werken. Rostr deelt niks met derden.",
        },
      ],
    },
    {
      id: "employee_faq",
      title: "Veelgestelde vragen",
      icon: HelpCircle,
      subtitle: "Direct antwoord op de meest gestelde vragen",
      articles: [
        {
          h: "Wat betekenen de tijden en 'netto' op mijn dienstkaartje?",
          p: [
            "Het grote getal is je totale werktijd (bijv. 09:00 – 17:00 = 8u bruto).",
            "'Netto' = wat je uitbetaald krijgt (bruto minus automatische pauze). Bijv. 8u bruto - 30 min pauze = 7,5u netto.",
            "'Geen pauze' betekent dat je dienst korter is dan de pauze-drempel van je bedrijf.",
          ],
        },
        {
          h: "Hoe zie ik in één oogopslag mijn hele week?",
          p: "Mijn rooster → je ziet alle 7 dagen onder elkaar. Vrije dagen zijn compact (dun regeltje), werkdagen als volledige kaart. Bovenaan kun je met de pijltjes vooruit/achteruit bladeren tussen weken.",
        },
        {
          h: "Verlof aanvragen — hoe werkt het?",
          p: [
            "Mijn verlof → 'Nieuwe aanvraag'.",
            "Kies begin- en einddatum, verlof-type (vakantie/ziek/onbetaald), optioneel een reden.",
            "Verstuur → je manager krijgt een melding en beoordeelt binnen enkele dagen.",
            "Statussen: 'Wachtend' (nog niet beoordeeld), 'Goedgekeurd', 'Afgewezen'. Bij afwijzing zie je de reden.",
          ],
        },
        {
          h: "Hoeveel verlofdagen heb ik nog?",
          p: "Mijn overzicht → widget 'Verlofsaldo' toont opgebouwd, opgenomen en resterend saldo. Waarschuwing bij < 3 dagen resterend.",
        },
        {
          h: "Kan ik een dienst met een collega ruilen?",
          p: "Ja — vanuit Mijn rooster klik je op een dienst → 'Ruilen' → kies specifieke collega of laat leeg. Manager keurt de definitieve wissel goed.",
        },
        {
          h: "Ik werkte langer/korter dan gepland — hoe geef ik dat door?",
          p: "Mijn rooster → open de dienst → 'Uren corrigeren'. Vul werkelijke start/eind in + korte toelichting. Manager keurt de correctie goed en jouw netto uren worden bijgewerkt.",
        },
        {
          h: "Krijg ik meldingen op mijn telefoon?",
          p: [
            "Als je Rostr aan je startscherm hebt toegevoegd (PWA) én push-meldingen hebt geaccepteerd: ja.",
            "Instellen: Profiel → Meldingen → schakel 'Push' aan.",
            "Nooit meldingen willen tijdens de nacht? Zet 'Rustige uren' aan met een van/tot tijd.",
          ],
        },
        {
          h: "Ik ben ziek — wat nu?",
          p: [
            "Bel of app je manager direct — dat gaat sneller dan een verlofaanvraag.",
            "Manager verwijdert je dienst en zoekt vervanging.",
            "Registreer voor de administratie een verlofaanvraag met type 'Ziek' zodat het in je overzicht klopt.",
          ],
        },
        {
          h: "Wat is offline modus?",
          p: "Als je geen internet hebt (metro, buitenland) blijft je laatst geladen rooster zichtbaar. Bovenin verschijnt een geel bandje 'Je bent offline'. Zodra je weer online komt, verversen de gegevens automatisch.",
        },
        {
          h: "Kan ik de app op mijn startscherm zetten?",
          p: [
            "iPhone: Safari → deel-icoon (kader met pijl) → 'Zet op beginscherm'.",
            "Android: Chrome vraagt automatisch bij bezoek 'Toevoegen aan startscherm' — accepteer.",
            "Werkt dan als een echte app: sneller, offline-bestendig, met push-meldingen.",
          ],
        },
        {
          h: "Ik zie een collega's verjaardag — kan ik feliciteren?",
          p: "Ja! Klik op de taart-emoji in de melding of op de verjaardag-widget → 'Feliciteer' → collega krijgt een leuk bericht met confetti-animatie.",
        },
        {
          h: "Hoe wissel ik van taal?",
          p: "Rechtsboven in de header → klik op 'NL' → kies Nederlands of English. Instelling wordt onthouden op al je apparaten.",
        },
        {
          h: "Waar vind ik hulp als ik er niet uitkom?",
          p: "Onderaan dit handboek staat een 'Contact ondersteuning'-knop met de e-mail van Rostr-support. Je manager kan meestal ook helpen met bedrijfsspecifieke vragen (verlofregels, uurloon, etc.).",
        },
      ],
    },
    {
      id: "news",
      title: "Nieuws & verjaardagen",
      icon: Megaphone,
      subtitle: "Wat er speelt in het bedrijf",
      articles: [
        {
          h: "Bedrijfsnieuws",
          p: "Berichten van je manager (openingstijden, evenementen, updates) verschijnen op de nieuws-pagina. Push-notificaties kun je aanzetten in je profiel.",
        },
        {
          h: "Zelf een bericht verbergen",
          p: "Ben je klaar met een bericht? Swipe het naar links — het verdwijnt alleen bij jou. Onderin verschijnt een knop om weggeswipete berichten weer terug te halen.",
        },
        {
          h: "Collega feliciteren",
          p: "Op iemands verjaardag zie je een 'Feest mee'-knop. Eén tik en de jarige krijgt jouw felicitatie als melding.",
        },
      ],
    },
    {
      id: "notifications",
      title: "Meldingen",
      icon: Bell,
      subtitle: "Blijf op de hoogte zonder de app open te hebben",
      articles: [
        {
          h: "Kanalen kiezen",
          p: [
            "App — verschijnt in het belletje-icoon rechtsboven zolang Rostr open is.",
            "E-mail — naar het adres op je profiel. Handig als je Rostr niet vaak open hebt.",
            "Push — pop-up op je telefoon of laptop, óók als Rostr niet actief is.",
          ],
        },
        {
          h: "Push aanzetten",
          p: "Voeg Rostr toe aan je startscherm (PWA) en sta pop-ups toe. Test daarna met de knop 'Test-melding' in Voorkeuren.",
        },
        {
          h: "Per categorie beheren",
          p: [
            "Verlof — beslissingen op jouw aanvragen.",
            "Ruilverzoeken — aanvragen van collega's en antwoorden.",
            "Rooster — nieuw gepubliceerd of je dienst gewijzigd.",
            "Nieuws — aankondigingen van je manager.",
            "Verjaardagen — collega's die vandaag jarig zijn.",
          ],
        },
      ],
    },
    {
      id: "profile",
      title: "Profiel & voorkeuren",
      icon: UserIcon,
      subtitle: "Jouw account, taal en thema",
      articles: [
        {
          h: "Wat kun je zelf zien?",
          p: "In je profiel zie je je naam, e-mail, telefoon, functie, afdeling, geboortedatum en huidige locatie/team. Zo weet je precies welke gegevens er van jou geregistreerd staan.",
        },
        {
          h: "Wat kan alleen management aanpassen?",
          p: [
            "Als medewerker is je profiel volledig 'read-only' — je manager of teamleider houdt je gegevens up-to-date.",
            "Klopt er iets niet? Vraag je manager het aan te passen — één klik en 't staat goed.",
            "Sommige velden zoals taal, thema en meldingsvoorkeuren beheer je natuurlijk wel zelf, in Voorkeuren.",
          ],
        },
        {
          h: "Voorkeuren",
          p: [
            "Taal — kies uit 2 talen (Nederlands en Engels), wisselt direct de hele app.",
            "Thema — licht of donker.",
            "Meldingen per categorie en kanaal (app / e-mail / push).",
          ],
        },
      ],
    },
  ];

  const FAQ_ALL = {
    id: "faq",
    title: "Veelgestelde vragen",
    icon: HelpCircle,
    subtitle: "De antwoorden die het vaakst nodig zijn",
    articles: [
      {
        h: "Ik krijg geen meldingen — wat nu?",
        p: [
          "Controleer in Profiel → Voorkeuren of push/e-mail aan staat voor de juiste categorie.",
          "iPhone/iPad: voeg Rostr toe aan je startscherm (Delen → Zet op beginscherm) en sta notificaties toe bij de eerste opening.",
          "Check je systeem-instellingen (Instellingen → Notificaties → Rostr).",
          "Test met de knop 'Test-melding' in Voorkeuren.",
        ],
      },
      {
        h: "Ik zie geen diensten — hoe kan dat?",
        p: "Diensten die nog op 'Concept' staan zijn onzichtbaar voor werknemers. Vraag je manager om de week te publiceren.",
      },
      {
        h: "Ik heb per ongeluk verkeerd verlof aangevraagd",
        p: "Zolang de aanvraag 'Wachtend' is kun je 'm annuleren via Mijn verlof. Is 'ie al goedgekeurd? Vraag je manager om te wijzigen.",
      },
      {
        h: "Kan ik Rostr op mijn telefoon installeren?",
        p: "Ja — Rostr is een PWA. iPhone: Safari → Delen → Zet op beginscherm. Android: Chrome vraagt automatisch 'Toevoegen aan startscherm'.",
      },
      {
        h: "Waar mail ik heen bij problemen?",
        p: "Alle rollen kunnen mailen naar rosterhelp@gmail.com voor persoonlijke ondersteuning. Zet je bedrijfsnaam en beschrijving erbij, dan helpen we je snel.",
      },
    ],
  };

  if (role === "owner") {
    return [...MANAGEMENT_SECTIONS, ...OWNER_ONLY, FAQ_ALL];
  }
  if (role === "manager" || role === "teamlead") {
    return [...MANAGEMENT_SECTIONS, FAQ_ALL];
  }
  return [...EMPLOYEE_SECTIONS, FAQ_ALL];
}

// ---- UI ---------------------------------------------------------------------

function renderParagraphs(p) {
  if (!p) return null;
  if (Array.isArray(p)) {
    // If items look like a checklist (start with a lowercase and end with .), render as bullets
    return (
      <ul className="mt-2 space-y-1.5 list-none">
        {p.map((item, i) => (
          <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed text-[hsl(var(--text))]">
            <span
              className="mt-[9px] w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: "hsl(var(--primary))" }}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="mt-2 text-[14px] leading-relaxed text-[hsl(var(--text))]">
      {p}
    </p>
  );
}

export default function Handbook() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);
  const sectionRefs = useRef({});

  const role = user?.role || "employee";
  const meta = ROLE_META[role] || ROLE_META.employee;
  const sections = useMemo(() => translateSections(content(role), lang), [role, lang]);

  // Filter sections by query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((sec) => {
        const articles = sec.articles.filter((a) => {
          const hay = [
            a.h,
            typeof a.p === "string" ? a.p : (Array.isArray(a.p) ? a.p.join(" ") : ""),
          ].join(" ").toLowerCase();
          return hay.includes(q);
        });
        const secMatch = (sec.title + " " + (sec.subtitle || "")).toLowerCase().includes(q);
        return secMatch ? sec : (articles.length ? { ...sec, articles } : null);
      })
      .filter(Boolean);
  }, [sections, query]);

  // Scrollspy — highlight the section closest to viewport top.
  // App uses a custom scroll container `[data-scroll-container]` in AppShell,
  // so we listen to its scroll events instead of window's.
  useEffect(() => { markHandbookViewed(); }, []);
  useEffect(() => {
    const container = document.querySelector("[data-scroll-container]") || window;
    const getScrollTop = () => (container === window ? window.scrollY : container.scrollTop);
    const handler = () => {
      const scrollY = getScrollTop() + 140;
      let current = filtered[0]?.id || null;
      for (const sec of filtered) {
        const el = sectionRefs.current[sec.id];
        if (el && el.offsetTop <= scrollY) current = sec.id;
      }
      setActiveId(current);
    };
    handler();
    container.addEventListener("scroll", handler, { passive: true });
    return () => container.removeEventListener("scroll", handler);
  }, [filtered]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const container = document.querySelector("[data-scroll-container]");
    if (container) {
      const top = el.offsetTop - 24;
      container.scrollTo({ top, behavior: "smooth" });
    } else {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="handbook-page" className="pb-24">
      <PageHeader
        eyebrow={t("handbook_eyebrow")}
        title={t("handbook_title")}
        actions={
          <span
            data-testid="handbook-role-badge"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[12px] font-bold border hairline"
            style={{
              background: `hsl(${meta.tint} / 0.14)`,
              color: `hsl(${meta.tint})`,
              borderColor: `hsl(${meta.tint} / 0.35)`,
            }}
          >
            <Shield size={12} strokeWidth={2.3} />
            {t(meta.key)}
          </span>
        }
      />

      {/* Hero card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="card p-5 md:p-6 mb-6"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 60%)",
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 text-white"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
              boxShadow: "0 10px 24px -8px hsl(var(--primary) / 0.55)",
            }}
          >
            <BookOpen size={20} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[18px] md:text-[20px] font-bold text-[hsl(var(--text-strong))] tracking-tight">
              {t("handbook_hero_title")}
            </div>
            <p className="mt-1 text-[13.5px] leading-relaxed text-[hsl(var(--text-muted))] max-w-2xl">
              {t("handbook_hero_desc")}
            </p>
          </div>
        </div>

        {/* Search — refined pill with clear button + result count */}
        <div className="mt-5">
          <div
            className={`group relative flex items-center gap-3 max-w-lg h-12 px-4 rounded-full border transition-all bg-white/70 backdrop-blur ${
              query ? "border-[hsl(var(--primary)/0.5)] shadow-[0_2px_10px_-2px_hsl(var(--primary)/0.25)]" : "hairline hover:border-[hsl(var(--primary)/0.35)]"
            }`}
          >
            <Search
              size={16}
              strokeWidth={2.2}
              className={`shrink-0 transition-colors ${query ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))]"}`}
            />
            <input
              data-testid="handbook-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("handbook_search_placeholder")}
              className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-[hsl(var(--text-muted))] text-[hsl(var(--text-strong))] tracking-tight"
            />
            {query && (
              <>
                <span className="hidden sm:inline text-[11px] font-mono tabular-nums text-[hsl(var(--text-muted))] shrink-0" data-testid="handbook-search-count">
                  {filtered.length} {filtered.length === 1 ? "sectie" : "secties"}
                </span>
                <button
                  onClick={() => setQuery("")}
              aria-label={t("clear_search")}
                  data-testid="handbook-search-clear"
                  className="w-7 h-7 rounded-full grid place-items-center text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))] hover:bg-[hsl(var(--muted))] transition shrink-0"
                >
                  <X size={13} strokeWidth={2.4} />
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Grid: TOC (desktop) + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
        {/* TOC — sticky on desktop */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] flex flex-col overflow-hidden">
            <div className="eyebrow mb-2 flex items-center gap-1.5 shrink-0">
              <Lightbulb size={11} strokeWidth={2.2} />
              {t("handbook_toc")}
            </div>
            <nav
              className="flex flex-col gap-0.5 overflow-y-auto pr-1 -mr-1 [scrollbar-width:thin]"
              data-testid="handbook-toc"
            >
              {filtered.map((sec) => {
                const Icon = sec.icon;
                const isActive = activeId === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    data-testid={`handbook-toc-${sec.id}`}
                    className={`group flex items-center gap-2 px-2.5 h-9 rounded-lg text-[13px] font-semibold text-left transition-colors ${
                      isActive
                        ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"
                        : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-strong))] hover:bg-[hsl(var(--muted))]"
                    }`}
                  >
                    <Icon size={13} strokeWidth={2.2} />
                    <span className="truncate">{sec.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-col gap-6" data-testid="handbook-content">
          {filtered.length === 0 && (
            <div className="card p-8 text-center">
              <HelpCircle size={22} className="mx-auto text-[hsl(var(--text-muted))] mb-2" />
              <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))]">
                {t("handbook_empty_title")}
              </div>
              <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1">
                {t("handbook_empty_hint")}
              </div>
            </div>
          )}

          {filtered.map((sec, secIdx) => {
            const Icon = sec.icon;
            return (
              <section
                key={sec.id}
                ref={(el) => (sectionRefs.current[sec.id] = el)}
                id={`handbook-${sec.id}`}
                data-testid={`handbook-section-${sec.id}`}
                className="card overflow-hidden"
              >
                <header className="p-5 md:p-6 border-b hairline flex items-start gap-3.5">
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                    style={{
                      background: "hsl(var(--primary-soft))",
                      color: "hsl(var(--primary))",
                    }}
                  >
                    <Icon size={16} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display text-[18px] md:text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))] leading-tight">
                      {sec.title}
                    </h2>
                    {sec.subtitle && (
                      <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-0.5">
                        {sec.subtitle}
                      </div>
                    )}
                  </div>
                </header>

                <div className="p-5 md:p-6 flex flex-col gap-5">
                  {sec.articles.map((a, i) => (
                    <article key={i} data-testid={`handbook-article-${sec.id}-${i}`}>
                      <h3 className="flex items-center gap-1.5 text-[14px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                        <ChevronRight size={13} className="text-[hsl(var(--primary))] shrink-0" strokeWidth={2.5} />
                        {a.h}
                      </h3>
                      {renderParagraphs(a.p)}
                    </article>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Footer tip with support email */}
          <div
            className="rounded-2xl border hairline p-4 flex items-start gap-3"
            style={{ background: "hsl(var(--muted) / 0.5)" }}
            data-testid="handbook-footer-tip"
          >
            <div className="w-8 h-8 rounded-lg bg-[hsl(var(--surface))] grid place-items-center shrink-0">
              <Lightbulb size={14} className="text-[hsl(var(--primary))]" strokeWidth={2.2} />
            </div>
            <div className="flex-1 text-[12.5px] leading-relaxed text-[hsl(var(--text-muted))]">
              <div>{t("handbook_footer_tip")}</div>
              <a
                href="mailto:rosterhelp@gmail.com"
                data-testid="handbook-support-email"
                className="mt-2 inline-flex items-center gap-1.5 text-[hsl(var(--primary))] font-semibold hover:underline"
              >
                rosterhelp@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
